import { escapeHtml } from '../ui/dom.ts'
import {
  BLOCKED_PLACEHOLDER,
  formatFileSize,
  KIND_AUDIO,
  KIND_FILE,
  KIND_GIF,
  mediaLabel,
  normalizeKind,
} from '../media/zionMediaProtocol.ts'
import { getMediaBytes, triggerDownload, bytesToDisplayUrl } from '../media/mediaCache.ts'
import {
  buildDocumentPreviewMarkup,
  clearDocumentPreviewCache,
  getPlainDocumentBytes,
} from '../media/documentPreview.ts'
import { openDocumentViewerModal } from '../media/documentViewer.ts'
import { buildVoiceBubbleHtml, wireVoiceBubbles } from '../ui/voiceMessageUi.ts'
import type { ChatMessage } from '../storage/chatStore.ts'

const objectUrls = new Map<string, string>()

export function revokeMediaObjectUrls(): void {
  for (const url of objectUrls.values()) URL.revokeObjectURL(url)
  objectUrls.clear()
  clearDocumentPreviewCache()
}

function mediaObjectUrl(cacheKey: string, bytes: Uint8Array, mime: string): string {
  let url = objectUrls.get(cacheKey)
  if (!url) {
    url = bytesToDisplayUrl(bytes, mime)
    objectUrls.set(cacheKey, url)
  }
  return url
}

async function resolveDocumentBytes(
  contactId: string,
  msg: ChatMessage,
  btn?: HTMLButtonElement,
): Promise<Uint8Array | null> {
  if (btn) {
    btn.disabled = true
    btn.textContent = '[DECOMPRESSING...]'
    btn.classList.add('media-doc-download-busy')
  }
  try {
    return await getPlainDocumentBytes(contactId, msg.id, msg.mediaGzip)
  } catch {
    return null
  } finally {
    if (btn) {
      btn.classList.remove('media-doc-download-busy')
      btn.disabled = false
    }
  }
}

export async function buildMediaBubbleHtml(m: ChatMessage, contactId: string): Promise<string> {
  if (!m.mediaKind && m.text === BLOCKED_PLACEHOLDER) {
    return `<p class="media-blocked">${escapeHtml(BLOCKED_PLACEHOLDER)}</p>`
  }
  if (!m.mediaKind) return ''

  const kind = normalizeKind(m.mediaKind)
  const bytes = await getMediaBytes(contactId, m.id)

  if (kind === KIND_FILE && m.pending && m.processingStatus) {
    const name = escapeHtml(m.mediaFileName || 'documento')
    const size = formatFileSize(m.mediaOrigSize ?? 0)
    const status = escapeHtml(m.processingStatus)
    return `
      <div class="media-doc-card media-doc-pending">
        <div class="media-doc-head">
          <span class="media-doc-icon" aria-hidden="true">📎</span>
          <div class="media-doc-meta">
            <span class="media-doc-name">${name}</span>
            <span class="media-doc-size">${escapeHtml(size)}</span>
            <span class="media-terminal-status">${status}</span>
          </div>
        </div>
      </div>
    `
  }

  if (!bytes) {
    return `<p class="media-loading">…</p>`
  }

  const mime = m.mediaMime || 'application/octet-stream'
  const cacheKey = `${contactId}:${m.id}`

  if (kind === KIND_AUDIO) {
    let playBytes = bytes
    let playMime = mime
    // iOS/Safari: WebM spesso resta a 0:00 — converti in WAV in lettura
    if (/webm|ogg/i.test(mime)) {
      try {
        const { encodeVoiceForAndroid } = await import('../media/voiceEncode.ts')
        const encoded = await encodeVoiceForAndroid(bytes, mime, m.audioDurationMs ?? 0)
        playBytes = encoded.bytes
        playMime = encoded.mime
      } catch {
        /* usa originale */
      }
    }
    return buildVoiceBubbleHtml(
      playBytes,
      playMime,
      cacheKey,
      objectUrls,
      m.audioDurationMs,
    )
  }

  if (kind === KIND_FILE) {
    const name = escapeHtml(m.mediaFileName || 'documento')
    const plainBytes = await getPlainDocumentBytes(contactId, m.id, m.mediaGzip)
    const size = formatFileSize(m.mediaOrigSize ?? plainBytes?.length ?? bytes.length)
    const previewHtml = plainBytes
      ? buildDocumentPreviewMarkup(plainBytes, mime, m.mediaFileName || 'documento', objectUrls, `${cacheKey}:preview`)
      : `<p class="media-doc-preview-error">Anteprima non disponibile</p>`

    return `
      <div class="media-doc-card">
        <div class="media-doc-head">
          <span class="media-doc-icon" aria-hidden="true">📎</span>
          <div class="media-doc-meta">
            <span class="media-doc-name">${name}</span>
            <span class="media-doc-size">${escapeHtml(size)}</span>
          </div>
        </div>
        ${previewHtml}
        <div class="media-doc-actions">
          <button type="button" class="media-doc-open" data-doc-id="${escapeHtml(m.id)}" data-doc-action="open">APRI</button>
          <button type="button" class="media-doc-download" data-doc-id="${escapeHtml(m.id)}" data-doc-action="download">SCARICA</button>
        </div>
      </div>
    `
  }

  if (kind === KIND_GIF || mime === 'image/gif' || mime.toLowerCase().includes('gif')) {
    const url = mediaObjectUrl(cacheKey, bytes, 'image/gif')
    // <img> standard: mantiene l'animazione (niente canvas / object-fit aggressivo in markup).
    return `<img class="media-img media-gif" src="${url}" alt="${escapeHtml(mediaLabel(KIND_GIF))}" decoding="async" />`
  }

  const url = mediaObjectUrl(cacheKey, bytes, mime)
  return `<img class="media-img" src="${url}" alt="${escapeHtml(mediaLabel(kind, m.mediaFileName))}" decoding="async" />`
}

export function wireMediaBubbleActions(root: HTMLElement, contactId: string, msgs: ChatMessage[]): void {
  wireVoiceBubbles(root)
  root.querySelectorAll<HTMLButtonElement>('.media-doc-open, .media-doc-download').forEach((btn) => {
    btn.addEventListener('click', () => {
      void (async () => {
        const id = btn.dataset.docId
        const action = btn.dataset.docAction
        if (!id || !action) return
        const msg = msgs.find((x) => x.id === id)
        if (!msg) return

        const prev = btn.textContent
        const bytes = await resolveDocumentBytes(contactId, msg, msg.mediaGzip ? btn : undefined)
        if (!bytes) {
          btn.textContent = 'ERRORE'
          window.setTimeout(() => {
            btn.textContent = prev
          }, 1500)
          return
        }

        const mime = msg.mediaMime || 'application/octet-stream'
        const fileName = msg.mediaFileName || 'zion-file'

        if (action === 'open') {
          openDocumentViewerModal(bytes, mime, fileName)
        } else {
          triggerDownload(bytes, mime, fileName)
        }

        btn.textContent = prev
      })()
    })
  })
}

export function mediaCaptionHtml(text: string, escape: (s: string) => string): string {
  const t = text.trim()
  if (!t || t === BLOCKED_PLACEHOLDER) return ''
  return `<p class="media-caption">${escape(t)}</p>`
}
